/**
 * Seed list of high-authority directories, PR sites, and citation targets
 * used by the Backlink Command Center (admin/backlinks).
 *
 * Admins mark each as: not_started | pending | submitted | live | rejected.
 * All entries are legitimate white-hat citation targets — no PBNs, no
 * paid link farms.
 */
export const DIRECTORY_SEED = [
  // High-authority business directories (DR 90+)
  { name: "Google Business Profile", url: "https://business.google.com/", category: "search", tier: 1 },
  { name: "Bing Places for Business", url: "https://www.bingplaces.com/", category: "search", tier: 1 },
  { name: "Apple Business Connect", url: "https://businessconnect.apple.com/", category: "search", tier: 1 },
  { name: "Better Business Bureau", url: "https://www.bbb.org/", category: "trust", tier: 1 },
  { name: "Trustpilot", url: "https://business.trustpilot.com/", category: "review", tier: 1 },
  { name: "Yelp for Business", url: "https://biz.yelp.com/", category: "review", tier: 1 },
  { name: "Chamber of Commerce", url: "https://www.chamberofcommerce.com/", category: "business", tier: 1 },
  { name: "Yellow Pages", url: "https://www.yellowpages.com/", category: "business", tier: 1 },
  { name: "Clutch", url: "https://clutch.co/", category: "B2B", tier: 1 },
  { name: "G2", url: "https://www.g2.com/", category: "B2B", tier: 1 },
  { name: "GoodFirms", url: "https://www.goodfirms.co/", category: "B2B", tier: 1 },
  { name: "Capterra", url: "https://www.capterra.com/", category: "B2B", tier: 1 },

  // Hawaii-local (for GBP + local SEO)
  { name: "Hawaii Chamber of Commerce", url: "https://www.cochawaii.org/", category: "local", tier: 2 },
  { name: "Honolulu Chamber", url: "https://www.cochonolulu.org/", category: "local", tier: 2 },
  { name: "Hawaii Business Directory", url: "https://www.hawaiibusiness.com/directory", category: "local", tier: 2 },
  { name: "BizHwaii", url: "https://www.bizhawaii.com/", category: "local", tier: 3 },

  // Legal / consumer-services directories
  { name: "Avvo (Non-Legal Services)", url: "https://www.avvo.com/", category: "consumer", tier: 2 },
  { name: "Justia (Business Directory)", url: "https://www.justia.com/", category: "consumer", tier: 2 },
  { name: "Consumer Affairs", url: "https://www.consumeraffairs.com/", category: "consumer", tier: 2 },
  { name: "Angi (Angie's List)", url: "https://www.angi.com/", category: "consumer", tier: 2 },
  { name: "Thumbtack", url: "https://www.thumbtack.com/", category: "consumer", tier: 2 },

  // Social / niche
  { name: "LinkedIn Company Page", url: "https://www.linkedin.com/company/", category: "social", tier: 1 },
  { name: "Facebook Business Page", url: "https://business.facebook.com/", category: "social", tier: 1 },
  { name: "Twitter/X Business", url: "https://business.twitter.com/", category: "social", tier: 1 },
  { name: "YouTube Channel", url: "https://youtube.com/", category: "social", tier: 1 },
  { name: "Reddit r/socialmedia (community)", url: "https://reddit.com/r/socialmedia", category: "social", tier: 2 },
  { name: "Quora Business", url: "https://www.quora.com/", category: "social", tier: 2 },
  { name: "Medium Publication", url: "https://medium.com/", category: "content", tier: 2 },
  { name: "Substack Newsletter", url: "https://substack.com/", category: "content", tier: 2 },

  // PR / press release
  { name: "PRWeb", url: "https://www.prweb.com/", category: "PR", tier: 2 },
  { name: "PR Newswire", url: "https://www.prnewswire.com/", category: "PR", tier: 1 },
  { name: "EIN Presswire", url: "https://www.einpresswire.com/", category: "PR", tier: 2 },
  { name: "Business Wire", url: "https://www.businesswire.com/", category: "PR", tier: 1 },
  { name: "PRLog", url: "https://www.prlog.org/", category: "PR", tier: 3 },
  { name: "OpenPR", url: "https://www.openpr.com/", category: "PR", tier: 3 },

  // Startup / tech
  { name: "Crunchbase", url: "https://www.crunchbase.com/", category: "startup", tier: 1 },
  { name: "AngelList (Wellfound)", url: "https://wellfound.com/", category: "startup", tier: 1 },
  { name: "Product Hunt", url: "https://www.producthunt.com/", category: "startup", tier: 1 },
  { name: "BetaList", url: "https://betalist.com/", category: "startup", tier: 2 },
  { name: "F6S", url: "https://www.f6s.com/", category: "startup", tier: 2 },
  { name: "Startup Ranking", url: "https://www.startupranking.com/", category: "startup", tier: 3 },

  // Niche: creator/social-media professional
  { name: "Creator Economy Reports", url: "https://creatoreconomy.so/", category: "niche", tier: 2 },
  { name: "Social Media Today", url: "https://www.socialmediatoday.com/", category: "niche", tier: 1 },
  { name: "Social Media Examiner", url: "https://www.socialmediaexaminer.com/", category: "niche", tier: 1 },

  // Podcast / audio (podcast interviews = high-authority backlinks)
  { name: "Podchaser", url: "https://www.podchaser.com/", category: "podcast", tier: 2 },
  { name: "MatchMaker.fm", url: "https://www.matchmaker.fm/", category: "podcast", tier: 2 },
  { name: "PodcastGuests.com", url: "https://podcastguests.com/", category: "podcast", tier: 3 },

  // HARO / expert quotes
  { name: "HARO (Help A Reporter Out)", url: "https://www.helpareporter.com/", category: "PR", tier: 1 },
  { name: "Qwoted", url: "https://www.qwoted.com/", category: "PR", tier: 2 },
  { name: "Terkel", url: "https://terkel.io/", category: "PR", tier: 2 },
  { name: "Featured.com", url: "https://featured.com/", category: "PR", tier: 2 },
  { name: "SourceBottle", url: "https://www.sourcebottle.com/", category: "PR", tier: 3 },

  // Local citation aggregators
  { name: "Data Axle (formerly InfoGroup)", url: "https://www.data-axle.com/", category: "aggregator", tier: 1 },
  { name: "Neustar Localeze", url: "https://www.neustarlocaleze.biz/", category: "aggregator", tier: 1 },
  { name: "Factual", url: "https://www.factual.com/", category: "aggregator", tier: 2 },
  { name: "Foursquare Places", url: "https://foursquare.com/", category: "aggregator", tier: 1 },
];
